import * as React from "react";
import { useRef, useEffect, useState } from "react";

const Logo = (props: React.SVGProps<SVGSVGElement>) => {
  const svgRef = useRef<SVGSVGElement>(null);
  
  // Refs for all 9 rectangles in the 3x3 grid
  // Left column
  const leftTopRef = useRef<SVGRectElement>(null);
  const leftMiddleRef = useRef<SVGRectElement>(null);
  const leftBottomRef = useRef<SVGRectElement>(null);
  
  // Middle column
  const middleTopRef = useRef<SVGRectElement>(null);
  const middleMiddleRef = useRef<SVGRectElement>(null);
  const middleBottomRef = useRef<SVGRectElement>(null);
  
  // Right column
  const rightTopRef = useRef<SVGRectElement>(null);
  const rightMiddleRef = useRef<SVGRectElement>(null);
  const rightBottomRef = useRef<SVGRectElement>(null);

  // Lazy load GSAP
  const [gsapInstance, setGsapInstance] = useState<any>(null);

  const loadGsap = async () => {
    if (gsapInstance) return gsapInstance;
    try {
      const { default: gsap } = await import("gsap");
      setGsapInstance(gsap);
      return gsap;
    } catch (e) {
      console.error("Failed to load GSAP for logo", e);
      return null;
    }
  };

  // Reconstruct grid structure from initial positions
  // Initial grid analysis:
  // - Column 1 (left): x=15, widths: 14, 14, 14 → max width = 14
  // - Column 2 (middle): x=32, widths: 8, 8, 8 → max width = 8
  // - Column 3 (right): x=43, widths: 6, 6, 6 → max width = 6
  // Gap between columns: 32 - (15+14) = 3, 43 - (32+8) = 3 → gap = 3
  // - Row 1 (top): y=15, heights: 6, 6, 6 → max height = 6
  // - Row 2 (middle): y=24, heights: 8, 8, 8 → max height = 8
  // - Row 3 (bottom): y=35, heights: 14, 14, 14 → max height = 14
  // Gap between rows: 24 - (15+6) = 3, 35 - (24+8) = 3 → gap = 3

  // Initial positions and sizes (left to right, top to bottom)
  // Dimensions divided by 2: 28x12, 16x12, 12x12, 28x16, 16x16, 12x16, 28x28, 16x28, 12x28
  const initialState = {
    // Left column
    leftTop: { x: 15, y: 15, width: 14, height: 6 },      // 28x12 / 2
    leftMiddle: { x: 15, y: 24, width: 14, height: 8 },    // 28x16 / 2
    leftBottom: { x: 15, y: 35, width: 14, height: 14 },   // 28x28 / 2
    
    // Middle column
    middleTop: { x: 32, y: 15, width: 8, height: 6 },     // 16x12 / 2
    middleMiddle: { x: 32, y: 24, width: 8, height: 8 }, // 16x16 / 2
    middleBottom: { x: 32, y: 35, width: 8, height: 14 }, // 16x28 / 2
    
    // Right column
    rightTop: { x: 43, y: 15, width: 6, height: 6 },       // 12x12 / 2
    rightMiddle: { x: 43, y: 24, width: 6, height: 8 },   // 12x16 / 2
    rightBottom: { x: 43, y: 35, width: 6, height: 14 },   // 12x28 / 2
  };

  // Calculate final grid structure from final dimensions
  // Final dimensions (left to right, top to bottom): 12x28, 16x28, 28x28, 12x16, 16x16, 28x16, 12x12, 18x12, 28x12
  // Divided by 2: 6x14, 8x14, 14x14, 6x8, 8x8, 14x8, 6x6, 9x6, 14x6
  // 
  // Column widths in final grid:
  // - Column 1: 6, 6, 6 → max = 6
  // - Column 2: 8, 8, 9 → max = 9
  // - Column 3: 14, 14, 14 → max = 14
  // Row heights in final grid:
  // - Row 1: 14, 14, 14 → max = 14
  // - Row 2: 8, 8, 8 → max = 8
  // - Row 3: 6, 6, 6 → max = 6
  //
  // Calculate column start positions (maintaining gap of 3):
  // - Column 1: x = 15 (same as initial)
  // - Column 2: x = 15 + 6 + 3 = 24
  // - Column 3: x = 24 + 9 + 3 = 36
  //
  // Calculate row start positions (maintaining gap of 3):
  // - Row 1: y = 15 (same as initial)
  // - Row 2: y = 15 + 14 + 3 = 32
  // - Row 3: y = 32 + 8 + 3 = 43

  // Hover state: Each rectangle stays in its grid position but transforms dimensions
  // Final grid (left to right, top to bottom): 12x28, 16x28, 28x28, 12x16, 16x16, 28x16, 12x12, 18x12, 28x12
  const hoverState = {
    // Position 1 (top-left): 28x12 → 12x28
    leftTop: { x: 15, y: 15, width: 6, height: 14 },      // 12x28 / 2
    
    // Position 2 (top-middle): 16x12 → 16x28
    middleTop: { x: 24, y: 15, width: 8, height: 14 },    // 16x28 / 2
    
    // Position 3 (top-right): 12x12 → 28x28
    rightTop: { x: 36, y: 15, width: 14, height: 14 },     // 28x28 / 2
    
    // Position 4 (middle-left): 28x16 → 12x16
    leftMiddle: { x: 15, y: 32, width: 6, height: 8 },     // 12x16 / 2
    
    // Position 5 (middle-middle): 16x16 → 16x16
    middleMiddle: { x: 24, y: 32, width: 8, height: 8 },  // 16x16 / 2
    
    // Position 6 (middle-right): 12x16 → 28x16
    rightMiddle: { x: 36, y: 32, width: 14, height: 8 },  // 28x16 / 2
    
    // Position 7 (bottom-left): 28x28 → 12x12
    leftBottom: { x: 15, y: 43, width: 6, height: 6 },     // 12x12 / 2
    
    // Position 8 (bottom-middle): 16x28 → 18x12
    middleBottom: { x: 24, y: 43, width: 9, height: 6 },  // 18x12 / 2
    
    // Position 9 (bottom-right): 12x28 → 28x12
    rightBottom: { x: 36, y: 43, width: 14, height: 6 },   // 28x12 / 2
  };

  // Animation timeline ref
  const timelineRef = useRef<any>(null);
  // Track hover state to prevent rapid toggling at edges
  const isHoveredRef = useRef<boolean>(false);
  const hoverTimeoutRef = useRef<number | null>(null);

  const animateToHover = async () => {
    if (!svgRef.current || isHoveredRef.current) return;

    // Load GSAP if not loaded
    const gsap = await loadGsap();
    if (!gsap) return;

    // Clear any pending timeout
    if (hoverTimeoutRef.current !== null) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Set hover state immediately to prevent rapid toggling
    isHoveredRef.current = true;

    // Kill any existing animation
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    // Create new timeline
    const tl = gsapInstance.timeline();
    timelineRef.current = tl;

    // Spring easing configuration (bouncy effect)
    const springConfig = {
      ease: "elastic.out(1, 0.5)",
      duration: 0.7,
    };

    // Animate all rectangles simultaneously
    if (leftTopRef.current) {
      tl.to(leftTopRef.current, {
        attr: hoverState.leftTop,
        ...springConfig,
      }, 0);
    }
    if (leftMiddleRef.current) {
      tl.to(leftMiddleRef.current, {
        attr: hoverState.leftMiddle,
        ...springConfig,
      }, 0);
    }
    if (leftBottomRef.current) {
      tl.to(leftBottomRef.current, {
        attr: hoverState.leftBottom,
        ...springConfig,
      }, 0);
    }
    if (middleTopRef.current) {
      tl.to(middleTopRef.current, {
        attr: hoverState.middleTop,
        ...springConfig,
      }, 0);
    }
    if (middleMiddleRef.current) {
      // Middle stays in place, but we still animate it for consistency
      tl.to(middleMiddleRef.current, {
        attr: hoverState.middleMiddle,
        ...springConfig,
      }, 0);
    }
    if (middleBottomRef.current) {
      tl.to(middleBottomRef.current, {
        attr: hoverState.middleBottom,
        ...springConfig,
      }, 0);
    }
    if (rightTopRef.current) {
      tl.to(rightTopRef.current, {
        attr: hoverState.rightTop,
        ...springConfig,
      }, 0);
    }
    if (rightMiddleRef.current) {
      tl.to(rightMiddleRef.current, {
        attr: hoverState.rightMiddle,
        ...springConfig,
      }, 0);
    }
    if (rightBottomRef.current) {
      tl.to(rightBottomRef.current, {
        attr: hoverState.rightBottom,
        ...springConfig,
      }, 0);
    }

    // Scale bounce on SVG container - subtle and natural
    if (svgRef.current) {
      tl.to(svgRef.current, {
        scale: 1.05,
        duration: 0.15,
        ease: "power2.out",
      }, 0)
      .to(svgRef.current, {
        scale: 1.0,
        duration: 0.4,
        ease: "elastic.out(1, 0.3)",
      }, 0.1); // Start bounce back sooner for more natural feel
    }
  };

  const animateToInitial = async () => {
    if (!svgRef.current || !isHoveredRef.current) return;

    const gsap = gsapInstance || await loadGsap();
    if (!gsap) return;

    // Clear any pending timeout
    if (hoverTimeoutRef.current !== null) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Mark as not hovered immediately (so animateToHover can be called again)
    // But delay the actual animation to prevent rapid toggling at edges
    isHoveredRef.current = false;

    hoverTimeoutRef.current = window.setTimeout(() => {
      // Double-check: if mouse re-entered during delay, don't animate out
      if (!svgRef.current || isHoveredRef.current) return;

      // Kill any existing animation
      if (timelineRef.current) {
        timelineRef.current.kill();
      }

      // Create new timeline
      const tl = gsapInstance.timeline();
      timelineRef.current = tl;

      // Spring easing configuration (bouncy effect)
      const springConfig = {
        ease: "elastic.out(1, 0.5)",
        duration: 0.7,
      };

      // Animate all rectangles back to initial positions
      if (leftTopRef.current) {
        tl.to(leftTopRef.current, {
          attr: initialState.leftTop,
          ...springConfig,
        }, 0);
      }
      if (leftMiddleRef.current) {
        tl.to(leftMiddleRef.current, {
          attr: initialState.leftMiddle,
          ...springConfig,
        }, 0);
      }
      if (leftBottomRef.current) {
        tl.to(leftBottomRef.current, {
          attr: initialState.leftBottom,
          ...springConfig,
        }, 0);
      }
      if (middleTopRef.current) {
        tl.to(middleTopRef.current, {
          attr: initialState.middleTop,
          ...springConfig,
        }, 0);
      }
      if (middleMiddleRef.current) {
        tl.to(middleMiddleRef.current, {
          attr: initialState.middleMiddle,
          ...springConfig,
        }, 0);
      }
      if (middleBottomRef.current) {
        tl.to(middleBottomRef.current, {
          attr: initialState.middleBottom,
          ...springConfig,
        }, 0);
      }
      if (rightTopRef.current) {
        tl.to(rightTopRef.current, {
          attr: initialState.rightTop,
          ...springConfig,
        }, 0);
      }
      if (rightMiddleRef.current) {
        tl.to(rightMiddleRef.current, {
          attr: initialState.rightMiddle,
          ...springConfig,
        }, 0);
      }
      if (rightBottomRef.current) {
        tl.to(rightBottomRef.current, {
          attr: initialState.rightBottom,
          ...springConfig,
        }, 0);
      }

      // Scale bounce on SVG container
      if (svgRef.current) {
        tl.to(svgRef.current, {
          scale: 0.95,
          duration: 0.15,
          ease: "power2.out",
        }, 0)
        .to(svgRef.current, {
          scale: 1.0,
          duration: 0.35,
          ease: "elastic.out(1, 0.5)",
        }, 0.15);
      }
    }, 50); // 50ms delay to prevent rapid toggling at edges
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      if (hoverTimeoutRef.current !== null) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <svg 
      ref={svgRef}
      width={64}
      height={64}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      onMouseEnter={animateToHover}
      onMouseLeave={animateToInitial}
      style={{ cursor: "pointer", willChange: "transform" }}
      {...props}
    >
      <rect
        width={64}
        height={64}
        // fill="#3E3E3E"
      />
      {/* Left column */}
      <rect
        ref={leftTopRef}
        x={15}
        y={15}
        width={14}
        height={6}
        rx={1.5}
        fill="white"
      />
      <rect 
        ref={leftMiddleRef}
        x={15}
        y={24}
        width={14}
        height={8}
        rx={1.5}
        fill="white"
      />
      <rect 
        ref={leftBottomRef}
        x={15}
        y={35}
        width={14}
        height={14}
        rx={1.5}
        fill="#FF6038"
      />
      {/* Middle column */}
      <rect 
        ref={middleTopRef}
        x={32}
        y={15}
        width={8}
        height={6}
        rx={1.5}
        fill="white"
      />
      <rect 
        ref={middleMiddleRef}
        x={32}
        y={24}
        width={8}
        height={8}
        rx={1.5}
        fill="#FFA411"
      />
      <rect 
        ref={middleBottomRef}
        x={32}
        y={35}
        width={8}
        height={14}
        rx={1.5}
        fill="white"
      />
      {/* Right column */}
      <rect 
        ref={rightTopRef}
        x={43}
        y={15}
        width={6}
        height={6}
        rx={1.5}
        fill="#2E8CFF"
      />
      <rect 
        ref={rightMiddleRef}
        x={43}
        y={24}
        width={6}
        height={8}
        rx={1.5}
        fill="white"
      />
      <rect 
        ref={rightBottomRef}
        x={43}
        y={35}
        width={6}
        height={14}
        rx={1.5}
        fill="white"
      />
    </svg>
  );
};

export default Logo;
